// Permission catalogue [MB D3; 02 §8.1/§8.3; BUILD-PLAN §9 additions and name alignment]. Keys are
// `module.verb`; special actions are never implied by `edit` [02 §8.1]. Grants are role-only.
import { z } from 'zod';

const STD5 = ['view', 'create', 'edit', 'delete', 'export'] as const;
const VCE = ['view', 'create', 'edit'] as const;
const VCEX = ['view', 'create', 'edit', 'export'] as const;

/** module → standard verbs + special actions / sensitive views. */
export const PERMISSION_CATALOGUE = {
  dashboard: { verbs: ['view'], special: [] },
  masters: { verbs: ['view', 'create', 'edit', 'delete'], special: [] },
  settings: { verbs: ['view', 'edit'], special: ['edit_past'] },
  sequences: { verbs: ['view', 'edit'], special: [] },
  customers: { verbs: STD5, special: ['view_commercial'] },
  suppliers: { verbs: STD5, special: ['view_banking', 'edit_banking'] },
  contractors: { verbs: STD5, special: [] },
  carriers: { verbs: STD5, special: [] },
  styles: { verbs: STD5, special: ['override', 'release_bom'] },
  specs: { verbs: VCEX, special: ['approve', 'approve_on_behalf'] },
  development: { verbs: VCE, special: ['approve', 'approve_on_behalf'] },
  costing: {
    verbs: VCEX,
    special: [
      'submit',
      'approve',
      'view_margin',
      'override',
      'close',
      'reopen',
      'post',
      'approve_on_behalf',
    ],
  },
  fabrics: { verbs: STD5, special: [] },
  trims: { verbs: STD5, special: [] },
  lots: { verbs: ['view', 'edit', 'export'], special: ['hold', 'release_hold', 'split'] },
  testing: { verbs: VCE, special: ['approve', 'approve_on_behalf'] },
  purchasing: {
    verbs: STD5,
    // CR-BG2c (CR-W2c-4): `eta_feed` = submit forwarder / file ETA feeds; held by integration API clients and system
    // adapters only, never by a person role (05 BR-ETA-009).
    special: ['approve', 'close', 'receive', 'reverse', 'override', 'approve_on_behalf', 'eta_feed'],
  },
  deliveries: { verbs: ['view', 'edit'], special: [] },
  orders: { verbs: STD5, special: ['confirm', 'revise', 'cancel', 'override'] },
  po_import: { verbs: ['view', 'create'], special: ['approve', 'reject'] },
  planning: { verbs: ['view', 'export'], special: ['run'] },
  programmes: { verbs: VCEX, special: ['approve', 'approve_on_behalf'] },
  dpp: { verbs: ['view', 'export'], special: [] },
  cutting: { verbs: STD5, special: ['confirm_fabric', 'confirm_trim', 'release', 'override'] },
  sewing: { verbs: VCE, special: ['issue', 'close'] },
  fg_receiving: { verbs: VCE, special: ['post', 'override'] },
  inventory: {
    verbs: ['view', 'create', 'export'],
    special: ['adjust', 'approve', 'override', 'view_value', 'approve_on_behalf'],
  },
  warehouse: { verbs: ['view', 'create', 'edit', 'delete'], special: ['apply', 'scan'] },
  // CR-BG2d (W2d.shipping R-RBAC-SHP-1): `scan` = scan-pack and loading scans from a registered scanner.
  shipping: { verbs: VCEX, special: ['dispatch', 'override', 'reissue_documents', 'scan'] },
  labels: { verbs: VCE, special: ['print', 'reprint'] },
  stickers: { verbs: VCE, special: ['print'] },
  notes: { verbs: VCE, special: ['approve', 'post', 'approve_on_behalf'] },
  d2c: {
    verbs: ['view', 'edit', 'export'],
    special: ['release_stock', 'pull_back', 'refund', 'fulfil'],
  },
  gl: { verbs: VCEX, special: ['approve', 'post', 'reverse', 'override', 'approve_on_behalf'] },
  ap: {
    verbs: VCEX,
    special: [
      'approve',
      'accept_variance',
      'post',
      'payment_run',
      'release_payment',
      'hold',
      'void',
      'override',
      'approve_on_behalf',
    ],
  },
  ar: {
    verbs: VCEX,
    // CR-BG2c (W2c.ar-bank R-CORE-AR-4): approving an invoice for a signed-invoice customer profile before dispatch.
    special: ['post_invoice', 'credit', 'apply_receipt', 'write_off', 'override', 'approve_dispatch'],
  },
  bank: { verbs: ['view', 'edit'], special: ['reconcile', 'close'] },
  tax: { verbs: VCE, special: [] },
  fx: { verbs: VCE, special: [] },
  periods: { verbs: ['view'], special: ['open', 'close', 'reopen'] },
  fin_reports: { verbs: ['view', 'export'], special: ['schedule'] },
  sage_bridge: { verbs: ['view', 'export'], special: ['generate', 'mark_imported'] },
  reports: { verbs: STD5, special: ['schedule', 'ask', 'view_personal'] },
  help: { verbs: ['view', 'edit'], special: [] },
  users: { verbs: ['view', 'create', 'edit', 'delete'], special: ['reset_mfa'] },
  roles: { verbs: ['view', 'create', 'edit', 'delete'], special: ['manage'] },
  audit: { verbs: ['view', 'export'], special: [] },
  devices: { verbs: ['view', 'create', 'delete'], special: [] },
  api_clients: { verbs: ['view', 'create', 'delete'], special: [] },
  ops: { verbs: ['view'], special: ['admin'] },
} as const satisfies Record<string, { verbs: readonly string[]; special: readonly string[] }>;

type Catalogue = typeof PERMISSION_CATALOGUE;
export type PermissionModule = keyof Catalogue;
export type PermissionKey = {
  [
    M in PermissionModule
  ]: `${M}.${Catalogue[M]['verbs'][number] | Catalogue[M]['special'][number]}`;
}[PermissionModule];

/** `core.permission.kind` [03 §3]. */
export type PermissionKind = 'standard' | 'special' | 'sensitive_view';

export interface PermissionDef {
  readonly key: PermissionKey;
  readonly module: PermissionModule;
  readonly verb: string;
  readonly kind: PermissionKind;
  /** i18n key for the role editor. */
  readonly labelKey: string;
  /** Needs a fresh step-up (re-auth) at action time. */
  readonly stepUp: boolean;
  /** Only exercisable from a registered device token (scanner) [02 §6.2 DEVICE_SCOPE]. */
  readonly deviceScoped: boolean;
}

/** Step-up actions: 02 §8.3 (`settings.edit_past`, `periods.reopen`), BR-PERM-004, 03 §18 payment run. */
export const STEP_UP_PERMISSIONS: ReadonlySet<string> = new Set([
  'settings.edit_past',
  'periods.reopen',
  'roles.manage',
  'ap.release_payment',
  // CR-W2a-14: bank-account edits and their verification (04 BR-PTN-024).
  'suppliers.edit_banking',
]);

export const DEVICE_SCOPED_PERMISSIONS: ReadonlySet<string> = new Set(['warehouse.scan', 'shipping.scan']);

function kindOf(verb: string, isStandard: boolean): PermissionKind {
  if (isStandard) return 'standard';
  return verb.startsWith('view_') ? 'sensitive_view' : 'special';
}

export const PERMISSIONS: readonly PermissionDef[] = Object.freeze(
  (Object.keys(PERMISSION_CATALOGUE) as PermissionModule[]).flatMap((module) => {
    const def = PERMISSION_CATALOGUE[module];
    const row = (verb: string, isStandard: boolean): PermissionDef => {
      const key = `${module}.${verb}` as PermissionKey;
      return Object.freeze({
        key,
        module,
        verb,
        kind: kindOf(verb, isStandard),
        labelKey: `permissions.${module}.${verb}`,
        stepUp: STEP_UP_PERMISSIONS.has(key),
        deviceScoped: DEVICE_SCOPED_PERMISSIONS.has(key),
      });
    };
    return [
      ...def.verbs.map((v: string) => row(v, true)),
      ...def.special.map((v: string) => row(v, false)),
    ];
  }),
);

export const PERMISSION_KEYS: readonly PermissionKey[] = Object.freeze(
  PERMISSIONS.map((p) => p.key),
);
const KEY_SET: ReadonlySet<string> = new Set(PERMISSION_KEYS);
const BY_KEY: ReadonlyMap<string, PermissionDef> = new Map(PERMISSIONS.map((p) => [p.key, p]));

export function isPermissionKey(value: unknown): value is PermissionKey {
  return typeof value === 'string' && KEY_SET.has(value);
}

export function permissionDef(key: PermissionKey): PermissionDef {
  return BY_KEY.get(key) as PermissionDef;
}

export const permissionKeySchema = z
  .string()
  .refine(isPermissionKey, { message: 'unknown permission' })
  .transform((v) => v as PermissionKey);

/** Route-level declaration [BR-PERM-001]: a catalogue key or one of the two sentinels. */
export type RoutePermission = PermissionKey | 'public' | 'authenticated';

export function isRoutePermission(value: unknown): value is RoutePermission {
  return value === 'public' || value === 'authenticated' || isPermissionKey(value);
}

/** `<module>.override` when the module has one (gate passes / retroactive corrections). */
export function overridePermissionOf(module: PermissionModule): PermissionKey | null {
  const key = `${module}.override`;
  return isPermissionKey(key) ? key : null;
}

/** Sensitive-view grants [02 §8.1]: the API omits the guarded fields without them. */
export const SENSITIVE_VIEW_PERMISSIONS: readonly PermissionKey[] = Object.freeze(
  PERMISSIONS.filter((p) => p.kind === 'sensitive_view').map((p) => p.key),
);
